import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Fingerprint,
  CloudArrowUp, FileText, FilePdf, Image as ImageIcon,
  Target, Compass, ArrowRight, Check, Warning, Spinner,
  Lightning, Brain, Eye, Crosshair, X
} from '@phosphor-icons/react';
import axios from 'axios';
import VoiceTextarea from './VoiceTextarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NoiseOverlay = () => <div className="noise-overlay" />;

// ==========================================
// SECTION B: FILE UPLOAD / TEXT
// ==========================================
const SectionUpload = ({ uploadedFiles, setUploadedFiles, cvText, setCvText, sessionId }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setUploadError('Nur PDF und Bilder erlaubt.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setUploadError('Max. 10MB pro Datei.');
      return;
    }

    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/${sessionId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedFiles(prev => [...prev, {
        name: file.name, size: file.size, type: file.type,
        path: response.data.storage_path, file_id: response.data.file_id
      }]);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Upload fehlgeschlagen.');
    } finally {
      setUploading(false);
    }
  };

  const handleMultiFiles = async (files) => {
    for (const file of files) {
      await handleFile(file);
    }
  };

  const removeFile = (idx) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) handleMultiFiles(files);
  };

  const fileTypeIcon = (type) => {
    if (type === 'application/pdf') return <FilePdf size={16} weight="fill" style={{ color: '#EF4444' }} />;
    if (type?.startsWith('image/')) return <ImageIcon size={16} weight="fill" style={{ color: '#3B82F6' }} />;
    return <FileText size={16} weight="fill" style={{ color: 'var(--text-secondary)' }} />;
  };

  return (
    <div className="mb-8" data-testid="section-upload">
      <div className="flex items-center gap-3 mb-4">
        <FileText size={24} weight="duotone" />
        <h3 className="text-overline">SEKTION B: KOMPETENZ-NACHWEISE</h3>
      </div>

      {/* Uploaded files list */}
      {uploadedFiles.length > 0 && (
        <div className="mb-3 space-y-1">
          {uploadedFiles.map((f, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
              className="flex items-center justify-between p-2"
              style={{ background: 'var(--bg-surface)', border: '1px solid var(--accent-green)' }}
              data-testid={`uploaded-file-${idx}`}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {fileTypeIcon(f.type)}
                <span className="text-xs truncate" style={{ color: '#22C55E' }}>{f.name}</span>
                <span className="text-mono flex-shrink-0" style={{ fontSize: '0.5rem', color: 'var(--text-secondary)' }}>
                  ({(f.size / 1024).toFixed(0)} KB)
                </span>
              </div>
              <button onClick={() => removeFile(idx)} className="ml-2 p-1 hover:bg-black/5 transition-all" data-testid={`remove-file-${idx}`}>
                <X size={12} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </motion.div>
          ))}
          <p className="text-mono" style={{ fontSize: '0.5rem', color: 'var(--text-secondary)' }}>
            {uploadedFiles.length} Dokument{uploadedFiles.length > 1 ? 'e' : ''} hochgeladen
          </p>
        </div>
      )}

      {/* Drag & Drop Zone */}
      <div onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className="relative p-6 text-center cursor-pointer transition-all mb-4"
        style={{
          background: dragOver ? 'rgba(255,255,255,0.05)' : 'var(--bg-surface)',
          border: `2px dashed ${dragOver ? 'var(--text-primary)' : 'var(--border-default)'}`,
        }}
        data-testid="upload-dropzone"
      >
        <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" multiple
          onChange={(e) => handleMultiFiles(Array.from(e.target.files || []))} data-testid="upload-input" />

        {uploading ? (
          <motion.div animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}>
            <Crosshair size={36} className="mx-auto mb-2" style={{ color: 'var(--accent-cyan)' }} />
            <p className="text-xs" style={{ color: 'var(--accent-cyan)' }}>RADAR-SCAN AKTIV...</p>
          </motion.div>
        ) : (
          <div>
            <CloudArrowUp size={36} className="mx-auto mb-2" style={{ color: 'var(--text-secondary)' }} />
            <p className="text-xs" style={{ color: 'var(--text-primary)' }}>
              {uploadedFiles.length > 0 ? 'Weitere Dokumente hochladen' : 'Dokumente hochladen'}
            </p>
            <p className="text-mono mt-1" style={{ fontSize: '0.55rem', color: 'var(--text-secondary)' }}>
              Lebenslauf · Zeugnisse · Arbeitszeugnisse · Zertifikate — PDF oder Bild · Max 10MB
            </p>
          </div>
        )}

        {uploadError && (
          <p className="text-xs mt-2" style={{ color: 'var(--accent-cyan)' }}>{uploadError}</p>
        )}
      </div>

      {/* Text Alternative */}
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
        <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>ODER MANUELL EINGEBEN</span>
        <div className="flex-1 h-px" style={{ background: 'var(--border-default)' }} />
      </div>

      <VoiceTextarea value={cvText} onChange={(e) => setCvText(e.target.value)}
        placeholder="Wichtigste Stationen, Lieblingsfaecher, relevante Erfahrungen..."
        rows={4}
        testId="cv-text-input" />
    </div>
  );
};

// ==========================================
// SECTION C: GOAL MODE (Multi-Goal with Time Commitment)
// ==========================================
const TIME_LABELS = [
  { value: 1, label: '1-2h/Woche' },
  { value: 2, label: '3-5h/Woche' },
  { value: 3, label: '5-10h/Woche' },
  { value: 4, label: '10-20h/Woche' },
  { value: 5, label: 'Vollzeit' }
];

const getTimeColor = (val) => {
  const pct = (val - 1) / 4;
  const r = Math.round(34 + pct * 187);
  const g = Math.round(197 - pct * 100);
  const b = Math.round(94 - pct * 50);
  return `rgb(${r},${g},${b})`;
};

const GoalItem = ({ goal, index, onUpdate, onRemove }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, height: 0 }}
    className="p-3 mb-2"
    style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)' }}
    data-testid={`goal-item-${index}`}
  >
    <div className="flex items-start justify-between gap-2 mb-2">
      <div className="flex items-center gap-2">
        <span className="text-mono font-bold" style={{ fontSize: '0.6rem', color: '#22C55E' }}>ZIEL {index + 1}</span>
      </div>
      <button onClick={() => onRemove(index)} className="p-1 hover:bg-black/5 transition-all"
        style={{ background: 'transparent', border: 'none', cursor: 'pointer' }} data-testid={`remove-goal-${index}`}>
        <X size={12} style={{ color: 'var(--text-secondary)' }} />
      </button>
    </div>
    <VoiceTextarea
      value={goal.text}
      onChange={(e) => onUpdate(index, 'text', e.target.value)}
      placeholder="Was willst du erreichen? So konkret wie moeglich."
      rows={2}
      testId={`goal-text-${index}`}
      className="mb-2"
    />
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-mono" style={{ fontSize: '0.5rem', color: 'var(--text-secondary)', letterSpacing: '0.1em' }}>ZEITAUFWAND</span>
        <span className="text-mono font-bold" style={{ fontSize: '0.6rem', color: getTimeColor(goal.time) }}>
          {TIME_LABELS.find(t => t.value === goal.time)?.label || ''}
        </span>
      </div>
      <input
        type="range" min="1" max="5" value={goal.time}
        onChange={(e) => onUpdate(index, 'time', parseInt(e.target.value))}
        className="w-full h-2 appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, #22C55E 0%, ${getTimeColor(goal.time)} ${((goal.time - 1) / 4) * 100}%, var(--border-default) ${((goal.time - 1) / 4) * 100}%, var(--border-default) 100%)`,
          borderRadius: 0, outline: 'none', WebkitAppearance: 'none'
        }}
        data-testid={`goal-time-${index}`}
      />
      <div className="flex justify-between mt-0.5">
        <span className="text-mono" style={{ fontSize: '0.4rem', color: 'var(--text-secondary)' }}>MINIMAL</span>
        <span className="text-mono" style={{ fontSize: '0.4rem', color: 'var(--text-secondary)' }}>VOLLZEIT</span>
      </div>
    </div>
  </motion.div>
);

const SectionGoal = ({ goalMode, setGoalMode, goals, setGoals }) => {
  const addGoal = () => {
    if (goals.length < 5) {
      setGoals([...goals, { text: '', time: 2 }]);
    }
  };

  const updateGoal = (index, field, value) => {
    setGoals(goals.map((g, i) => i === index ? { ...g, [field]: value } : g));
  };

  const removeGoal = (index) => {
    setGoals(goals.filter((_, i) => i !== index));
  };

  return (
    <div className="mb-8" data-testid="section-goal">
      <div className="flex items-center gap-3 mb-4">
        <Target size={24} weight="duotone" />
        <h3 className="text-overline">SEKTION C: ZIEL-MODUS</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <motion.button whileTap={{ scale: 0.98 }} onClick={() => { setGoalMode('have_goal'); if (goals.length === 0) setGoals([{ text: '', time: 2 }]); }}
          className="p-6 text-left transition-all"
          style={{
            background: goalMode === 'have_goal' ? 'rgba(34,197,94,0.08)' : 'var(--bg-surface)',
            border: `2px solid ${goalMode === 'have_goal' ? '#22C55E' : 'var(--border-default)'}`
          }}
          data-testid="goal-have">
          <Target size={32} className="mb-3" weight="duotone" style={{ color: goalMode === 'have_goal' ? '#22C55E' : 'var(--text-secondary)' }} />
          <p className="text-mono font-bold mb-1" style={{ color: goalMode === 'have_goal' ? '#22C55E' : 'var(--text-primary)' }}>
            ICH HABE EIN ZIEL
          </p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Ich weiss, wohin ich will. Zeig mir, ob meine Hardware dafuer reicht.
          </p>
        </motion.button>

        <motion.button whileTap={{ scale: 0.98 }} onClick={() => setGoalMode('discover')}
          className="p-6 text-left transition-all"
          style={{
            background: goalMode === 'discover' ? 'rgba(139,92,246,0.08)' : 'var(--bg-surface)',
            border: `2px solid ${goalMode === 'discover' ? '#8B5CF6' : 'var(--border-default)'}`
          }}
          data-testid="goal-discover">
          <Compass size={32} className="mb-3" weight="duotone" style={{ color: goalMode === 'discover' ? '#8B5CF6' : 'var(--text-secondary)' }} />
          <p className="text-mono font-bold mb-1" style={{ color: goalMode === 'discover' ? '#8B5CF6' : 'var(--text-primary)' }}>
            ZEIG MIR MEINEN WEG
          </p>
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            Die KI analysiert alle Bloecke und generiert Vorschlaege.
          </p>
        </motion.button>
      </div>

      <AnimatePresence>
        {goalMode === 'have_goal' && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
            {goals.map((goal, i) => (
              <GoalItem key={i} goal={goal} index={i} onUpdate={updateGoal} onRemove={removeGoal} />
            ))}
            {goals.length < 5 && (
              <button onClick={addGoal}
                className="w-full py-2 flex items-center justify-center gap-2 transition-all hover:bg-black/5"
                style={{ border: '1px dashed var(--border-default)', background: 'transparent', cursor: 'pointer' }}
                data-testid="add-goal-btn"
              >
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>+ WeiterES ZIEL (MAX 5)</span>
              </button>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ==========================================
// LOADING SCREEN (Radar Scan)
// ==========================================
const ProjectionLoading = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-void)' }}>
    <NoiseOverlay />
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center max-w-md">
      {/* Radar Animation */}
      <div className="relative w-48 h-48 mx-auto mb-8">
        <svg viewBox="0 0 200 200" className="w-full h-full">
          <circle cx="100" cy="100" r="90" fill="none" stroke="var(--border-default)" strokeWidth="0.5" />
          <circle cx="100" cy="100" r="60" fill="none" stroke="var(--border-default)" strokeWidth="0.5" />
          <circle cx="100" cy="100" r="30" fill="none" stroke="var(--border-default)" strokeWidth="0.5" />
          <line x1="100" y1="10" x2="100" y2="190" stroke="var(--border-default)" strokeWidth="0.5" />
          <line x1="10" y1="100" x2="190" y2="100" stroke="var(--border-default)" strokeWidth="0.5" />
          <motion.line x1="100" y1="100" x2="100" y2="10" stroke="#22C55E" strokeWidth="1.5"
            style={{ transformOrigin: '100px 100px' }}
            animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} />
          <motion.circle cx="135" cy="65" r="3" fill="#22C55E" animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }} />
          <motion.circle cx="70" cy="130" r="3" fill="#22C55E" animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 1.2 }} />
          <motion.circle cx="150" cy="110" r="3" fill="#8B5CF6" animate={{ opacity: [0, 1, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.8 }} />
        </svg>
      </div>

      <h2 className="text-xl font-bold mb-3">STRATEGISCHE PROJEKTION</h2>
      <motion.p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}
        animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 2, repeat: Infinity }}>
        Die KI analysiert alle Assessment-Daten...
      </motion.p>

      <div className="space-y-2 text-left" style={{ maxWidth: '300px', margin: '0 auto' }}>
        {['GEIST-Dossier laden', 'KOERPER-HEI verarbeiten', 'WESEN-Profil integrieren', 'Projektion generieren'].map((step, idx) => (
          <motion.div key={step} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: idx * 1.5 }}
            className="flex items-center gap-2">
            <motion.div animate={{ opacity: [0.3, 1, 0.3] }}
              transition={{ duration: 1, repeat: Infinity, delay: idx * 0.3 }}
              className="w-1.5 h-1.5 rounded-full" style={{ background: '#22C55E' }} />
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>{step}</span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  </div>
);

// ==================== MAIN COMPONENT ====================
const StrategicProjection = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ name: '', gender: '', life_status: '' });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [cvText, setCvText] = useState('');
  const [goalMode, setGoalMode] = useState('');
  const [goals, setGoals] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [checkingExisting, setCheckingExisting] = useState(true);

  // Load name + gender + lebensstatus from Feld 1 status
  useEffect(() => {
    const loadFeld1 = async () => {
      try {
        const res = await axios.get(`${API}/user/status/${sessionId}`);
        if (res.data?.feld1) {
          const f = res.data.feld1;
          setProfile(p => ({
            ...p,
            name: f.name || '',
            gender: f.geschlecht || '',
            life_status: f.lebensstatus || ''
          }));
        }
      } catch {}
    };
    loadFeld1();
  }, [sessionId]);

  // Check if projection already exists → redirect to results
  useEffect(() => {
    const checkExisting = async () => {
      try {
        const res = await axios.get(`${API}/user/projection-status/${sessionId}`);
        if (res.data?.status === 'done') {
          navigate(`/projection/${sessionId}`, { replace: true });
          return;
        }
        if (res.data?.status === 'generating') {
          setSubmitting(true);
          // Start polling
          for (let i = 0; i < 60; i++) {
            await new Promise(r => setTimeout(r, 5000));
            const poll = await axios.get(`${API}/user/projection-status/${sessionId}`);
            if (poll.data?.status === 'done') { navigate(`/projection/${sessionId}`, { replace: true }); return; }
            if (poll.data?.status === 'error') break;
          }
          setSubmitting(false);
        }
      } catch {}
      setCheckingExisting(false);
    };
    checkExisting();
  }, [sessionId, navigate]);

  const canSubmit = goalMode;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('name', profile.name);
      formData.append('gender', profile.gender);
      formData.append('life_status', profile.life_status);
      formData.append('goal_mode', goalMode);
      formData.append('goal_text', JSON.stringify(goals));
      formData.append('cv_text', cvText);
      formData.append('uploaded_filenames', uploadedFiles.map(f => f.name).join('|||'));

      // Start generation (returns immediately)
      await axios.post(`${API}/user/final-projection`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 15000
      });

      // Poll for result
      const maxAttempts = 60; // 5 minutes max
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise(r => setTimeout(r, 5000));
        try {
          const res = await axios.get(`${API}/user/projection-status/${sessionId}`);
          if (res.data.status === 'done') {
            navigate(`/projection/${sessionId}`);
            return;
          }
          if (res.data.status === 'error') {
            setError(res.data.message || 'Fehler bei der Projektion.');
            setSubmitting(false);
            return;
          }
        } catch {}
      }
      setError('Zeitlimit ueberschritten. Bitte erneut versuchen.');
      setSubmitting(false);
    } catch (err) {
      console.error('Projection error:', err);
      setError('Fehler beim Starten der Projektion.');
      setSubmitting(false);
    }
  };

  if (submitting) return <ProjectionLoading />;

  return (
    <div className="page-shell-bg" data-testid="strategic-projection">
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="w-full max-w-2xl page-shell-card">
        <div className="page-shell-header">
          <div className="flex items-center gap-3">
            <div className="hex-clip flex items-center justify-center" style={{ width: 40, height: 40, background: 'var(--accent-cyan)' }}>
              <Eye size={20} weight="bold" color="#fff" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Strategische Projektion</h2>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Finale Bestandsaufnahme</p>
            </div>
          </div>
        </div>
        <div className="page-shell-body">
          <div className="text-center mb-8">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Vervollstaendige dein Profil. Die KI kombiniert alle Daten zu einer strategischen Projektion.
            </p>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <SectionUpload uploadedFiles={uploadedFiles} setUploadedFiles={setUploadedFiles}
              cvText={cvText} setCvText={setCvText} sessionId={sessionId} />
            <SectionGoal goalMode={goalMode} setGoalMode={setGoalMode} goals={goals} setGoals={setGoals} />
          </motion.div>

          {error && (
            <div className="mb-4 p-3 flex items-center gap-2" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid var(--accent-cyan)', borderRadius: '12px' }}>
              <Warning size={20} style={{ color: 'var(--accent-cyan)' }} />
              <p className="text-sm" style={{ color: 'var(--accent-cyan)' }}>{error}</p>
            </div>
          )}

          <button onClick={handleSubmit} disabled={!canSubmit}
            className="page-shell-btn" data-testid="projection-submit">
            <Brain size={20} weight="bold" />
            Strategische Projektion generieren
            <ArrowRight size={18} weight="bold" />
          </button>

          {!canSubmit && (
            <p className="text-xs text-center mt-2" style={{ color: 'var(--text-secondary)' }}>
              Ziel-Modus erforderlich
            </p>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default StrategicProjection;
